/* eslint-disable @typescript-eslint/indent */
import styled from 'styled-components';

import { ThemeProp } from '~/theme/Themes';

export const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
`;

export const Title = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
`;

export const Subtitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const ColumnRow = styled.div<{ $highlighted?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${(props: ThemeProp & { $highlighted?: boolean }) =>
      props.$highlighted ? props.theme.colors.primary.c300 : props.theme.colors.neutrals.c100};
`;

export const ColumnName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  overflow-wrap: anywhere;
`;

export const ColumnHint = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: normal;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const TypePicker = styled.div`
  width: 9.5rem;
  flex-shrink: 0;
`;

export const Badge = styled.span`
  flex-shrink: 0;
  padding: 0.125rem 0.5rem;
  border-radius: 0.375rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
  background: ${(props: ThemeProp) => props.theme.colors.primary.c050};
`;

export const HelperText = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const RuleCard = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid
    ${(props: ThemeProp & { $expanded?: boolean }) =>
      props.$expanded ? props.theme.colors.primary.c500 : props.theme.colors.neutrals.c100};
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
`;

export const RuleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const IconButton = styled.button<{ $rotated?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: none;
  background: none;
  cursor: pointer;
  stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  transform: rotate(${({ $rotated }: { $rotated?: boolean }) => ($rotated ? '0deg' : '-90deg')});
`;

export const RuleTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  overflow-wrap: anywhere;
`;

export const AllOfLabel = styled.span`
  margin-right: 0.375rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const ColumnLabel = styled.span`
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
`;

export const AttributeLabel = styled.span`
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

// The icon carries the warning color; the text stays neutral, legible on a light background
export const WarningLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};

  svg {
    flex-shrink: 0;
    color: ${(props: ThemeProp) => props.theme.colors.system.warning.c500};
  }
`;

export const WarningText = styled.p`
  margin: 0;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};

  strong {
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

export const OrSeparator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  text-transform: uppercase;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }
`;

export const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`;

export const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.primary.c500};

  &:disabled {
    cursor: default;
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
  }
`;

export const DroppedRulesList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.75rem 0 0;
  padding-left: 1.25rem;
`;
/* eslint-enable @typescript-eslint/indent */
